import { graphql } from '@packages/gql/generated/shopping';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from 'urql';
import { useRecoElement } from '../../../helpers/useRecoElement';
import type { ProductsRecoSliderProps, RdeType, RequestParams } from '../../../types';
import { RecoCarousel } from '../../RecoCarousel';
import { RecoProductCard } from '../../RecoProductCard';
import { useProductsRecoTracking } from '../../RecoProductCard/useProductsRecoTracking';
import { RecoProductCardSkeleton } from '../../RecoProductCardSkeleton';

export type PrudsysCategoryToProductsRecoSliderProps = Pick<RequestParams, 'categoryId'> &
  ProductsRecoSliderProps;

const PrudsysCategoryToProductsDocument = graphql(/* GraphQL */ `
  query PrudsysCategoryToProducts($locale: String!, $categoryId: String!) {
    prudsysCategoryToProducts(locale: $locale, categoryId: $categoryId) {
      title
      type
      recommendations {
        ...ProductRecommendationFragment
        product {
          akl
        }
      }
      extendedViewUrl
    }
  }
`);

const RDE_TYPE: RdeType = 'prudsysCategoryToProducts';

/**
 * PrudsysCategoryToProductsProductsRecoSlider component description displayed in storybook
 * */
export const PrudsysCategoryToProductsRecoSlider: FC<PrudsysCategoryToProductsRecoSliderProps> = ({
  categoryId,
  elementIndex = 0,
  fetchOnThreshold = false,
  pageTemplate,
  preferTitle = false,
  title,
}) => {
  const { locale } = useIntl();
  const { isInThreshold, isInView, recoEl } = useRecoElement();

  const [{ data, error }] = useQuery({
    query: PrudsysCategoryToProductsDocument,
    variables: {
      categoryId,
      locale: locale!,
    },
    context: useMemo(() => ({ suspense: false }), []),
    pause: fetchOnThreshold ? !isInThreshold : false,
  });

  const recommendations = data ? data.prudsysCategoryToProducts.recommendations : undefined;

  const rdeTypeAbbr = data?.prudsysCategoryToProducts.type;

  useProductsRecoTracking({
    rdeTypeAbbr,
    pageTemplate,
    elementIndex,
    recommendations,
    trackingCondition: isInView && !!recommendations && recommendations.length > 0,
  });

  if ((error && !recommendations) || (recommendations && recommendations.length === 0)) {
    return null;
  }

  return (
    <RecoCarousel
      title={preferTitle ? title : data?.prudsysCategoryToProducts.title || title}
      ref={recoEl}
      loadingState={!recommendations}
    >
      {!recommendations
        ? // rule is disabled because order doesn't change between rerenders
          // eslint-disable-next-line react/no-array-index-key
          [...Array(3)].map((_, i) => <RecoProductCardSkeleton key={i} />)
        : recommendations
            .filter((recommendation) => recommendation.product)
            .map((recommendation, idx) => (
              <RecoProductCard
                pageTemplate={pageTemplate}
                // rule is disabled because order doesn't change between rerenders
                // eslint-disable-next-line react/no-array-index-key
                key={idx}
                elementIndex={elementIndex}
                position={idx}
                recommendationData={recommendation}
                productsLength={recommendations.length}
                rdeType={RDE_TYPE}
              />
            ))}
    </RecoCarousel>
  );
};
